import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap"; // Keeping Spinner for simplicity or replace with Icon
import { FaApple, FaExternalLinkAlt, FaChartLine } from "react-icons/fa"; 
import CountryList from "../CountryList";
import { appleApi } from "../../services/api";

const Overview = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countryCode, setCountryCode] = useState("");
  const [searchingCountry, setSearchingCountry] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async (country = "") => {
    setLoading(true);
    try {
      const params = country ? { country_code: country } : {};
      const { data: result } = await appleApi.get("trending-apps/", { params });
      const appsData = result?.trending_apps ?? result?.apps ?? [];

      setApps(appsData);

      // Extract unique categories
      const categoryCounts = appsData.reduce((acc, app) => {
        acc[app.category] = (acc[app.category] || 0) + 1;
        return acc;
      }, {});

      const sortedCategories = Object.keys(categoryCounts).sort((a, b) => categoryCounts[b] - categoryCounts[a]);

      setCategories(sortedCategories);
      setSelectedCategory(sortedCategories[0] || "");
    } catch (error) {
      console.error("Error fetching apps:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCountrySearch = () => {
    if (countryCode.trim()) {
      setSearchingCountry(true);
      fetchApps(countryCode).finally(() => setSearchingCountry(false));
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 mb-8 text-white shadow-xl relative overflow-hidden">
        <div className="relative z-10">
            <h1 className="text-3xl font-bold mb-2 flex items-center">
                <FaApple className="mr-3 text-4xl text-gray-300" />
                Market Overview
            </h1>
            <p className="text-gray-400 max-w-2xl">
                Explore trending apps across different categories and countries. Gain insights into what's popular right now on the App Store.
            </p>
        </div>
        <div className="absolute right-0 top-0 h-full w-1/3 bg-white/5 skew-x-12 transform translate-x-1/2"></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
        <div className="md:col-span-5">
             <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Select Country</label>
                <div className="flex gap-2">
                     <div className="flex-grow">
                         <CountryList onSelect={(selectedCountry) => setCountryCode(selectedCountry)} />
                     </div>
                     <button 
                        onClick={handleCountrySearch} 
                        disabled={searchingCountry} 
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md shadow-sm transition-colors disabled:opacity-50 text-sm"
                     >
                        {searchingCountry ? "..." : "Search"}
                     </button>
                </div>
             </div>
        </div>

        <div className="md:col-span-7">
             <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 h-full flex flex-col justify-center">
                 <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Filter by Category</label>
                 {categories.length > 0 ? (
                    <select 
                        className="w-full bg-gray-50 border border-gray-200 text-gray-700 py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={selectedCategory} 
                        onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                        {categories.map((category, index) => (
                            <option key={index} value={category}>{category}</option>
                        ))}
                    </select>
                 ) : (
                     <p className="text-sm text-gray-400 italic">Loading categories...</p>
                 )}
             </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800 flex items-center">
            <span className="bg-blue-100 text-blue-700 p-2 rounded-lg mr-3">
                <FaChartLine />
            </span>
            Trending Apps <span className="ml-2 text-gray-400 font-normal text-base">in {selectedCategory}</span>
        </h2>
      </div>

      {loading ? (
        <div className="flex flex-col justify-center items-center py-20">
          <Spinner animation="border" variant="primary" className="mb-4" />
          <p className="text-gray-500 animate-pulse">Fetching market data...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {apps
            .filter((app) => app.category === selectedCategory)
            .map((app, index) => (
              <div key={index} className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden flex flex-col h-full transform hover:-translate-y-1">
                <div className="p-6 flex flex-col items-center flex-grow">
                    <div className="relative mb-4">
                        <img 
                            src={app.icon || "https://via.placeholder.com/150"} 
                            alt={app.name} 
                            className="w-20 h-20 rounded-2xl shadow-md object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute -bottom-2 -right-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm">
                            #{index + 1}
                        </div>
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-800 text-center leading-tight mb-2 line-clamp-2 h-12" title={app.name}>
                        {app.name}
                    </h3>
                    <p className="text-sm text-gray-500 text-center mb-4 line-clamp-1">
                        {app.developer}
                    </p>
                </div>
                
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-between items-center">
                    <span className="text-xs font-semibold text-gray-500 bg-white border border-gray-200 px-2 py-1 rounded">
                        {app.category}
                    </span>
                    <a 
                        href={app.link} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center transition-colors"
                    >
                        View <FaExternalLinkAlt className="ml-1 text-xs" />
                    </a>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default Overview;

